import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BankFeedSyncBanner - Persistent, non-blocking banner that shows
 * real-time progress when bank transactions are being fetched and classified.
 *
 * Shown at the top of: Bank Transactions Center, My Banks, Settings > Banking.
 * Auto-polls backend, auto-dismisses when complete.
 */
export function BankFeedSyncBanner({ bankAccounts = [] }) {
  const [progresses, setProgresses] = useState({});
  const [dismissed, setDismissed] = useState({});
  const pollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const monoAccountIds = bankAccounts.filter(a => a.open_banking_connected).map(a => a.id).join(',');

  const fetchProgress = useCallback(async () => {
    const monoAccounts = bankAccounts.filter(a => a.open_banking_connected);
    if (!monoAccounts.length) return;

    const updates = {};
    for (const acct of monoAccounts) {
      try {
        const { data } = await axios.get(`${API}/api/bank-feeds/${acct.id}/sync-progress`);
        // iter877 - Show the banner while:
        //   * a sync/classification is in flight (existing behaviour), OR
        //   * there is at least one live ``review_needed`` row on this
        //     account (work-queue mode). When the user clears the queue,
        //     the banner disappears automatically - no more stale "2 need
        //     your review" lingering after that batch is done.
        const liveReviewNeeded = data.live_review_needed_count || 0;
        if (
          data.is_fetching ||
          data.classification_status === 'failed' ||
          liveReviewNeeded > 0
        ) {
          updates[acct.id] = data;
        }
      } catch {
        // Silently ignore - account may not have sync data
      }
    }
    setProgresses(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monoAccountIds]);

  useEffect(() => {
    fetchProgress();
    pollRef.current = setInterval(fetchProgress, 6000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchProgress]);

  // iter877 - Auto-dismiss the work-queue banner is no longer time-based.
  // The banner naturally disappears the instant the live
  // ``live_review_needed_count`` drops to 0 (the user clears the queue
  // by approving/posting transactions). We keep the timer only for the
  // ``failed`` variant so it doesn't shout forever.
  useEffect(() => {
    const timers = [];
    Object.entries(progresses).forEach(([id, p]) => {
      if (p.classification_status === 'failed' && !dismissed[id]) {
        const t = setTimeout(() => setDismissed(prev => ({ ...prev, [id]: true })), 30000);
        timers.push(t);
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [progresses, dismissed]);

  // Jul 16 - one banner per state per page. This component now only owns:
  //   fetching (blue), review-needed (green), failed (red).
  // The "AI is classifying…" variant was REMOVED: every page that mounts
  // this banner also mounts ClassificationProgressTracker, which shows the
  // same information with a real progress bar - two banners saying the
  // same thing (or worse, contradicting) confused users.
  const activeBanners = Object.entries(progresses).filter(
    ([id, p]) => !dismissed[id] && (
      p.is_fetching ||
      p.classification_status === 'failed' ||
      (p.live_review_needed_count || 0) > 0
    )
  );

  if (!activeBanners.length) return null;

  const isOnBankFeedCenter = location.pathname.includes('/bank-feed');
  // On Settings > Banking the global BacklogProgressBanner already shows the
  // fetch phase - skip the duplicate blue variant there.
  const isOnSettings = location.pathname.includes('/settings');

  return (
    <div className="space-y-2 mb-4" data-testid="bank-feed-sync-banner">
      {activeBanners.map(([id, progress]) => {
        const { is_fetching, classification_status, bank_name } = progress;
        const liveReviewNeeded = progress.live_review_needed_count || 0;

        // Sep 2026 · the blue "Fetching…" strip duplicated the green
        // BacklogProgressBanner for Mono flows - the green one is canonical.
        if (is_fetching && !isOnSettings) return null;

        // "Work queue" banner: shown whenever there's at least
        // one live ``review_needed`` row, regardless of whether the most
        // recent batch is "completed" or not. Copy + count are live -
        // they match the AI Bank Transactions Center KPI strip exactly. The
        // banner clears itself as soon as the user empties the queue.
        // Jul 14 - suppressed ON the Bank Transactions Center itself: the KPI
        // strip already shows the same count there (information overload).
        if (liveReviewNeeded > 0 && !isOnBankFeedCenter) {
          return (
            <div key={id} className="flex items-center gap-3 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-lg px-4 py-3" data-testid={`sync-banner-done-${id}`}>
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  {liveReviewNeeded} transaction{liveReviewNeeded === 1 ? '' : 's'} need{liveReviewNeeded === 1 ? 's' : ''} your review at {bank_name || 'your bank'}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                  Open the Bank Transactions Center to categorise and post them to your ledger.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!isOnBankFeedCenter && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-green-700 hover:text-green-900 hover:bg-green-100 text-xs h-7"
                    onClick={() => navigate('/bank-feeds')}
                    data-testid={`sync-banner-go-feed-${id}`}
                  >
                    Review <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                )}
                <button
                  onClick={() => setDismissed(prev => ({ ...prev, [id]: true }))}
                  className="text-green-400 hover:text-green-600 p-0.5"
                  data-testid={`sync-banner-dismiss-${id}`}
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        }

        if (classification_status === 'failed') {
          return (
            <div key={id} className="flex items-center gap-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3" data-testid={`sync-banner-failed-${id}`}>
              <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Classification failed for {bank_name || 'your bank'}
                </p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                  Transactions were fetched but classification encountered an error. Try re-syncing.
                </p>
              </div>
              <button
                onClick={() => setDismissed(prev => ({ ...prev, [id]: true }))}
                className="text-red-400 hover:text-red-600 p-0.5"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}
